import { useEffect, useMemo, useRef, useState } from 'react';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Input } from '@/components/Input';
import { track } from '@/lib/analytics';
import { estimateChildSupport } from '@/lib/child-support/formulas';
import {
  CHILD_SUPPORT_STATES,
  getStateConfigBySlug,
  isStateSlug,
  type ChildSupportStateSlug,
} from '@/lib/child-support/schemas';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl, type UtmParams } from '@/lib/urls';

type Payer = 'you' | 'coparent' | 'none';

type Props = {
  defaultStateSlug?: string;
};

function formatCurrency(amount: number): string {
  return `$${Math.max(0, Math.round(amount)).toLocaleString()}`;
}

function formatPayer(payer: Payer): string {
  if (payer === 'you') return 'You';
  if (payer === 'coparent') return 'Your co-parent';
  return 'No payment';
}

/** Bridge copy that connects the estimate they just saw to Harbor's core value. */
function getBridgeCopy(payer: Payer, amount: number): { headline: string; sub: string } {
  if (payer === 'you' && amount > 0) {
    return {
      headline: `Make every ${formatCurrency(amount)} count`,
      sub: 'Log each payment automatically and keep a court-ready record so a number is never your word against theirs.',
    };
  }
  if (payer === 'coparent' && amount > 0) {
    return {
      headline: `Make sure that ${formatCurrency(amount)} actually shows up`,
      sub: 'Track every payment, flag what is late, and keep a clear paper trail, all in one place with your co-parent.',
    };
  }
  return {
    headline: 'Support is only part of the picture',
    sub: 'Childcare, medical, and school costs add up fast. Split and track shared expenses fairly in Harbor.',
  };
}

export function ChildSupportCalculator({ defaultStateSlug }: Props) {
  const initialSlug: ChildSupportStateSlug = isStateSlug(defaultStateSlug ?? '')
    ? (defaultStateSlug as ChildSupportStateSlug)
    : 'california';

  const [stateSlug, setStateSlug] = useState<ChildSupportStateSlug>(initialSlug);
  const [numChildren, setNumChildren] = useState(1);
  const [yourMonthlyIncome, setYourMonthlyIncome] = useState(5500);
  const [coparentMonthlyIncome, setCoparentMonthlyIncome] = useState(4500);
  const [yourParentingTimePercent, setYourParentingTimePercent] = useState(50);
  const [utm] = useState<UtmParams>(() => getUtmParamsFromUrl());
  const hasInteracted = useRef(false);
  const isFirstRender = useRef(true);

  const stateConfig = getStateConfigBySlug(stateSlug);
  const estimate = useMemo(
    () =>
      estimateChildSupport({
        stateSlug,
        numChildren,
        yourMonthlyIncome,
        coparentMonthlyIncome,
        yourParentingTimePercent,
      }),
    [stateSlug, numChildren, yourMonthlyIncome, coparentMonthlyIncome, yourParentingTimePercent],
  );

  // Fire "Started" once on the first real input change (not on mount, which
  // would just duplicate the page-view event), then debounce the calculated
  // event so rapid edits/keystrokes log a single meaningful estimate.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!hasInteracted.current) {
      hasInteracted.current = true;
      track('Child Support Calculator Started', { state: stateSlug });
    }
    const timer = setTimeout(() => {
      track('Child Support Estimate Calculated', {
        state: stateSlug,
        num_children: numChildren,
        payer: estimate.payer,
        monthly_amount: estimate.monthlyAmount,
        model_type: estimate.modelType,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [stateSlug, numChildren, yourMonthlyIncome, coparentMonthlyIncome, yourParentingTimePercent, estimate]);

  const officialCalculatorUrl = stateConfig?.officialCalculatorUrl;
  const bridge = getBridgeCopy(estimate.payer, estimate.monthlyAmount);

  const handleStoreClick = (store: 'app_store' | 'google_play') => {
    track('Child Support Harbor CTA Clicked', {
      source: store,
      state: stateSlug,
      payer: estimate.payer,
      monthly_amount: estimate.monthlyAmount,
      num_children: numChildren,
    });
    const w = window as Window & {
      gtagSendEvent?: (eventName: string) => void;
      fbq?: (...args: unknown[]) => void;
    };
    if (typeof w.gtagSendEvent === 'function') {
      w.gtagSendEvent(store === 'app_store' ? 'click_app_store' : 'click_google_play');
    }
    if (typeof w.fbq === 'function') {
      w.fbq('track', 'Lead');
    }
  };

  return (
    <div className='grid gap-6 lg:grid-cols-[1.5fr_1fr]'>
      <div className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
        <h2 className='text-2xl font-bold text-gray-900'>Child support estimate calculator</h2>
        <p className='mt-2 text-sm text-gray-600'>
          Estimate only, not legal advice. This is intentionally lighter than official worksheets.
        </p>

        <div className='mt-6 grid gap-4 md:grid-cols-2'>
          <div className='md:col-span-2'>
            <label className='mb-1 block text-sm font-medium text-gray-700'>State</label>
            <select
              className='w-full rounded-xl border border-gray-200 bg-white px-4 py-3'
              value={stateSlug}
              onChange={(e) => {
                if (isStateSlug(e.target.value)) {
                  setStateSlug(e.target.value);
                }
              }}
            >
              {CHILD_SUPPORT_STATES.map((state) => (
                <option key={state.code} value={state.slug}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>

          <Input
            type='number'
            min={1}
            max={4}
            label='Number of children (4 = 4+)'
            value={numChildren}
            onChange={(e) => setNumChildren(Math.max(1, Number(e.target.value || 1)))}
          />
          <Input
            type='number'
            min={0}
            step='100'
            label='Your monthly income'
            value={yourMonthlyIncome}
            onChange={(e) => setYourMonthlyIncome(Math.max(0, Number(e.target.value || 0)))}
          />
          <Input
            type='number'
            min={0}
            step='100'
            label='Co-parent monthly income'
            value={coparentMonthlyIncome}
            onChange={(e) => setCoparentMonthlyIncome(Math.max(0, Number(e.target.value || 0)))}
          />

          <div>
            <label className='mb-1 block text-sm font-medium text-gray-700'>Your parenting time</label>
            <select
              className='w-full rounded-xl border border-gray-200 bg-white px-4 py-3'
              value={yourParentingTimePercent}
              onChange={(e) => setYourParentingTimePercent(Number(e.target.value))}
            >
              <option value={50}>About 50/50</option>
              <option value={65}>Mostly me (~65%)</option>
              <option value={35}>Mostly co-parent (~35%)</option>
              <option value={80}>Primary me (~80%)</option>
              <option value={20}>Primary co-parent (~20%)</option>
            </select>
          </div>
        </div>

        <div className='mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-800'>
          State formulas vary and can include deductions, childcare, health insurance, and other
          factors not captured here. Always verify with your state&apos;s official calculator.
        </div>
      </div>

      <aside className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm'>
        <p className='text-sm text-gray-500'>Estimated monthly support</p>
        <p className='mt-2 text-4xl font-bold text-gray-900'>{formatCurrency(estimate.monthlyAmount)}</p>
        <p className='mt-2 text-sm text-gray-700'>
          Payer: <strong>{formatPayer(estimate.payer)}</strong>
        </p>

        <ul className='mt-4 space-y-2 text-sm text-gray-600'>
          {estimate.notes.map((note) => (
            <li key={note}>- {note}</li>
          ))}
        </ul>

        <div className='mt-6 rounded-xl border border-primary/20 bg-primary/5 p-4'>
          <p className='text-base font-semibold text-gray-900'>{bridge.headline}</p>
          <p className='mt-1 text-sm text-gray-600'>{bridge.sub}</p>

          <div className='mt-4 space-y-3'>
            <a
              href={getAppStoreUrl('child-support-calculator', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => handleStoreClick('app_store')}
              className='flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-semibold text-white transition-colors hover:bg-primary-hover'
            >
              <FaApple size={18} />
              <span>Get Harbor on iPhone</span>
            </a>
            <a
              href={getPlayStoreUrl('child-support-calculator', utm)}
              target='_blank'
              rel='noopener noreferrer'
              onClick={() => handleStoreClick('google_play')}
              className='flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-3 font-semibold text-white transition-colors hover:bg-gray-800'
            >
              <FaGooglePlay size={18} />
              <span>Get Harbor on Android</span>
            </a>
          </div>
        </div>

        {officialCalculatorUrl ? (
          <a
            href={officialCalculatorUrl}
            target='_blank'
            rel='noreferrer'
            onClick={() =>
              track('Child Support Official Calculator Clicked', { state: stateSlug })
            }
            className='mt-4 block text-sm font-medium text-primary underline'
          >
            Official state calculator/resource
          </a>
        ) : null}
      </aside>
    </div>
  );
}
